import React from 'react';
import { CaretRight, ListNumbers, Lightning, Trophy, Sparkle, Check } from '@phosphor-icons/react';
import ManageEventPill from './ManageEventPill';

// Shared row renderers for the "My Events" list. Used by both the Home
// accordion (capped at 3) and the Events page "My Events" tab (no cap).

export function LadderRow({ data, onClick }) {
  const { rank, wins, losses, recent } = data;
  const recordText =
    wins != null && losses != null ? `${wins}W · ${losses}L` : null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="my-events-ladder-row"
      className="w-full text-left bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl p-3 flex items-center justify-between gap-2 hover:bg-[#f0ece3]/40"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-[#015e3f]/10 text-[#015e3f] flex items-center justify-center">
          <ListNumbers size={18} weight="fill" />
        </div>
        <div className="min-w-0">
          <div className="text-sm text-[#013523] font-medium truncate flex items-center gap-2">
            Men's Singles Ladder
            <span
              className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#015e3f]/10 text-[#015e3f]"
              data-testid="my-events-ladder-rank"
            >
              #{rank}
            </span>
          </div>
          <div className="text-[11px] text-[#6a766e] mt-0.5 flex flex-wrap gap-2">
            {recent ? (
              <span data-testid="my-events-ladder-recent">
                {recent.outcome} {recent.preposition} {recent.opponent}
              </span>
            ) : recordText ? (
              <span>{recordText}</span>
            ) : (
              <span>You're on the ladder</span>
            )}
          </div>
        </div>
      </div>
      <CaretRight size={16} className="text-[#6a766e] shrink-0" />
    </button>
  );
}

export function SessionRow({ session: s, onClick, canManage = false, onManage }) {
  // Compose sub-line: Court N · Week X of Y · Men's Night
  const parts = [];
  if (s.status === 'in_progress') {
    if (s.my_court_number) parts.push(`Court ${s.my_court_number}`);
    parts.push(`Week ${s.current_week} of ${s.weeks_planned}`);
  } else if (s.status === 'registration_open') {
    parts.push(s.my_registration?.role === 'sub' ? 'Sub Pool · waiting' : 'Registered · waiting to start');
  } else if (s.status === 'registration_closed') {
    parts.push(s.my_registration?.role === 'sub' ? 'Sub Pool · awaits start' : 'Registered · awaits start');
  }
  parts.push(s.gender === 'mens' ? "Men's Night" : 'Ladies Night');

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`my-event-${s.id}`}
      className="w-full text-left bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl p-3 flex items-center justify-between gap-2 hover:bg-[#f0ece3]/40"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-[#015e3f]/10 text-[#015e3f] flex items-center justify-center">
          <Trophy size={18} weight="fill" />
        </div>
        <div className="min-w-0">
          <div className="text-sm text-[#013523] font-medium truncate">{s.name}</div>
          <div className="text-[11px] text-[#6a766e] mt-0.5 flex flex-wrap gap-2 items-center">
            {s.is_alternate && (
              <span
                data-testid={`my-event-alt-${s.id}`}
                className="inline-flex items-center text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5"
              >
                Alternate
              </span>
            )}
            {parts.map((p, i) => (
              <span key={i}>{p}</span>
            ))}
          </div>
        </div>
      </div>
      {canManage ? (
        <span
          data-testid={`my-event-manage-${s.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onManage?.(s);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onManage?.(s);
            }
          }}
          className="shrink-0 inline-flex items-center gap-1 bg-[#c2a251] hover:bg-[#b6923c] text-[#013523] text-[11px] font-semibold rounded-full px-2.5 py-1 cursor-pointer select-none"
        >
          Manage Event
        </span>
      ) : (
        <CaretRight size={16} className="text-[#6a766e] shrink-0" />
      )}
    </button>
  );
}


// Club Event row — mirrors SessionRow's visual language so RSVP'd club
// events sit cleanly alongside Tournaments / Round Robins / Sessions in
// the unified My Events list. Payment badges are informational only —
// RSVP alone qualifies a club event for My Events.
const CATEGORY_LABEL = {
  social: 'Social', tennis: 'Tennis', charity: 'Charity', club: 'Club',
};

function _fmtWhen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${date} · ${time}`;
  } catch { return null; }
}

export function ClubEventRow({ event: ce, onClick }) {
  const when = _fmtWhen(ce.starts_at);
  const location = ce.location_text || null;
  const category = ce.category ? CATEGORY_LABEL[ce.category] : null;
  const paymentEnabled = !!ce.payment_enabled;
  const paid = !!ce.viewer_paid;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`my-event-club-${ce.id}`}
      className="w-full text-left bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl p-3 flex items-center justify-between gap-2 hover:bg-[#f0ece3]/40"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-[#c9a93a]/15 text-[#7c5b1a] flex items-center justify-center">
          <Sparkle size={18} weight="fill" />
        </div>
        <div className="min-w-0">
          <div className="text-sm text-[#013523] font-medium truncate flex items-center gap-2">
            <span className="truncate">{ce.name}</span>
            {category && (
              <span
                data-testid={`my-event-club-${ce.id}-category`}
                className="shrink-0 inline-flex items-center text-[10px] uppercase tracking-wider bg-[#f0e6cf] text-[#7c5b1a] border border-[#c9a93a]/40 rounded-full px-1.5 py-0.5"
              >
                {category}
              </span>
            )}
          </div>
          <div className="text-[11px] text-[#6a766e] mt-0.5 flex flex-wrap gap-x-2 gap-y-1 items-center">
            <span data-testid={`my-event-club-${ce.id}-registered`}>Registered</span>
            {when && <span>· {when}</span>}
            {location && <span>· {location}</span>}
            {paymentEnabled && (
              paid ? (
                <span
                  data-testid={`my-event-club-${ce.id}-paid`}
                  className="inline-flex items-center gap-0.5 text-[10.5px] font-semibold text-[#015e3f] bg-[#e6f2ec] border border-[#015e3f]/25 rounded-full px-1.5 py-0.5"
                >
                  <Check size={10} weight="bold" /> Paid
                </span>
              ) : (
                <span
                  data-testid={`my-event-club-${ce.id}-payment-due`}
                  className="inline-flex items-center text-[10.5px] font-semibold text-[#7c5b1a] bg-[#f5eed8] border border-[#c9a93a]/40 rounded-full px-1.5 py-0.5"
                >
                  Payment Due
                </span>
              )
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <ManageEventPill eventKind="club_event" eventId={ce.id} onClick={(e) => e.stopPropagation()} />
        <CaretRight size={16} className="text-[#6a766e]" />
      </div>
    </button>
  );
}


// Active Challenges row — visually consistent with LadderRow / SessionRow.
// Used on the Home accordion so the "Active Challenges" content matches the
// rest of the My Events tiles.
export function ChallengeRow({ rank, statusText, subText, ctaLabel, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="home-challenges-row"
      className="w-full text-left bg-[#fbfaf8] border border-[#e3dcd1] rounded-xl p-3 flex items-center justify-between gap-2 hover:bg-[#f0ece3]/40"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-[#015e3f]/10 text-[#015e3f] flex items-center justify-center">
          <Lightning size={18} weight="fill" />
        </div>
        <div className="min-w-0">
          <div className="text-sm text-[#013523] font-medium truncate flex items-center gap-2">
            <span data-testid="home-challenges-status-text">{statusText}</span>
            {rank ? (
              <span
                data-testid="home-challenges-rank-pill"
                className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#015e3f]/10 text-[#015e3f]"
              >
                #{rank}
              </span>
            ) : null}
          </div>
          {subText ? (
            <div className="text-[11px] text-[#6a766e] mt-0.5 truncate">{subText}</div>
          ) : null}
        </div>
      </div>
      {ctaLabel ? (
        <span
          data-testid="home-challenges-cta"
          className="shrink-0 inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-[#015e3f] text-white text-xs font-medium"
        >
          {ctaLabel} →
        </span>
      ) : (
        <CaretRight size={16} className="text-[#6a766e] shrink-0" />
      )}
    </button>
  );
}
